import { observer } from "mobx-react-lite";
import { FC, useEffect, useRef } from "react";
import { Box, Card, CardHeader, makeStyles, Link } from "@material-ui/core";
import { store } from "../store";
import { Loading } from "./Loading";
import { Text } from "./Text";
import { useIdentityContext } from "react-netlify-identity";
import SlickCarousel from "react-slick";
import {
  ChevronLeft,
  ChevronRight,
  Info,
  WarningOutlined,
} from "@material-ui/icons";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { slideSettings } from "../constants/configs";
import colors from "../constants/colors";
import DesignCard from "./DesignCard";

const UserDesigns: FC = observer(() => {
  const classes = styles();
  const carouselRef = useRef(null);
  const { fetchDesigns, myDesigns } = store;

  const { user } = useIdentityContext();

  useEffect(() => {
    fetchDesigns(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!myDesigns) {
    return <Loading text="Finding your designs" />;
  }

  const prevArrowClick = () => {
    carouselRef?.current?.slickPrev();
  };
  const nextArrowClick = () => {
    carouselRef?.current?.slickNext();
  };

  return (
    <>
      <Box display="flex" justifyContent="space-between">
        <Text color="secondary" bold>
          Compare
        </Text>
        <Box display="flex">
          <Box
            marginRight="20px"
            onClick={prevArrowClick}
            className={classes.carouselArrowWrapper}
          >
            <ChevronLeft className={classes.carouselArrow} />
          </Box>

          <Box
            onClick={nextArrowClick}
            className={classes.carouselArrowWrapper}
          >
            <ChevronRight className={classes.carouselArrow} />
          </Box>
        </Box>
      </Box>
      {myDesigns?.length && !user && (
        <Box
          bgcolor={colors.grey}
          marginTop={2}
          marginBottom={1}
          marginX={2}
          p={2}
          borderRadius={15}
        >
          <WarningOutlined
            style={{ verticalAlign: "bottom", marginRight: "0.5em" }}
          />
          <Link
            component="button"
            variant="body2"
            onClick={() => document.getElementById("signup").scrollIntoView()}
          >
            Sign up
          </Link>
          {" or "}
          <Link variant="body2" href="/login">
            Sign in
          </Link>{" "}
          to save these designs to your user account, otherwise they will be
          lost
        </Box>
      )}

      <Box
        bgcolor={colors.grey}
        marginTop={1}
        marginBottom={2}
        marginX={2}
        p={2}
        borderRadius={15}
      >
        <Info style={{ verticalAlign: "bottom", marginRight: "0.5em" }} />
        Select up to 2 previous designs to compare with your most recent
        calculation
      </Box>

      <Box id="designs" style={{ overflow: "scroll" }}>
        {myDesigns?.length > 1 ? (
          <SlickCarousel {...slideSettings} ref={carouselRef}>
            {myDesigns.map(
              (design, index) =>
                index > 0 && (
                  <DesignCard
                    key={design.created + design._id}
                    design={design}
                  />
                )
            )}
          </SlickCarousel>
        ) : (
          <Card className={classes.card}>
            <CardHeader
              title="No designs to compare yet"
              subheader="When you create more designs they will appear here"
            />
          </Card>
        )}
      </Box>
    </>
  );
});

const styles = makeStyles((theme) => ({
  slider: { display: "flex", flexDirection: "row", maxWidth: 1000 },
  container: {
    padding: 40,
    alignItems: "center",
    justifyContent: "center",
    display: "flex",
    flexDirection: "column",
  },
  card: {
    margin: "1em",
    cursor: "pointer",
    [theme.breakpoints.down("sm")]: {
      margin: 0,
    },
  },
  cardContent: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
  },
  text: { textTransform: "uppercase" },
  field: {
    margin: "0.5em 1em",
    width: 200,
    display: "flex",
    flexGrow: 1,
  },
  actions: {
    justifyContent: "flex-end",
  },
  isSelected: {
    borderColor: "red",
    borderWidth: 1,
    borderStyle: "solid",
  },
  carouselArrow: {
    fontSize: 50,
    color: theme.palette.primary.main,
    [theme.breakpoints.down("sm")]: {
      fontSize: 30,
    },
  },
  carouselArrowWrapper: {
    cursor: "pointer",
  },
}));

export default UserDesigns;
